import React, { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { createPackageIncident } from '../../api/packages';
import { showToast } from '../../utils/showToast';
import { apiErrorCode, apiErrorStatus } from '../../utils/apiErrorMessage';
import {
  INCIDENT_IDEMPOTENCY_KEY_REUSED,
  INCIDENTS_ERROR_SOURCE,
  incidentsApiErrorText,
} from '../../utils/incidentsApiErrors';
import {
  INCIDENT_SEVERITY,
  INCIDENT_RESPONSIBLE,
  getAutoSeverity,
  getRequiredDepartments,
  DEPARTMENT_LABELS,
} from '../../constants/incidents';
import { Button } from '../ui/Button';
// Las ayudas (?) del formulario son las del panel de incidencias, su único
// contenedor: el globo va en un portal fijo. Con ui/InfoHint (globo absolute de
// 320 px anclado al ícono) quedaban cortadas por el borde de la lista con scroll
// del panel, que mide de 280 a 440 px.
import PanelHint from '../helpdeskIncidents/PanelHint';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { cn } from '../../utils/cn';
import IncidentTypePicker, { selectableIncidentTypes } from './IncidentTypePicker';
import { useIdempotencyKey } from './useIdempotencyKey';
import { useRecentIncidentTypes } from './useRecentIncidentTypes';

// Tope explícito del alta: el default de axios es de 60 s y, con la clave de
// idempotencia, reintentar tras un timeout es seguro.
export const INCIDENT_CREATE_TIMEOUT_MS = 15000;

// 16 px en móvil: con menos, iOS hace zoom al enfocar el campo.
const MOBILE_FIELD_TEXT = 'text-base md:text-sm';
const FIELD_LABEL = 'text-sm font-medium text-fg';

const SEVERITY_OPTIONS = Object.entries(INCIDENT_SEVERITY).map(([value, label]) => ({ value, label }));
const RESPONSIBLE_OPTIONS = Object.entries(INCIDENT_RESPONSIBLE).map(([value, label]) => ({ value, label }));

/**
 * Huella del intento lógico: los mismos campos que viajan en el alta. Si no
 * cambia, un reintento lleva la misma clave de idempotencia.
 */
function buildFingerprint({ packageId, type, description, severity, responsible, origin }) {
  return JSON.stringify({
    packageId,
    type,
    description: description.trim(),
    severity: severity || null,
    responsible: responsible || null,
    origin: origin ?? null,
  });
}

/** Qué dice un alta fallida sobre la incidencia y sobre la clave que llevaba. */
const SUBMIT_FAILURE = Object.freeze({
  // Sin respuesta (red caída, timeout, CORS) o 5xx: no se sabe si se creó.
  unknown: 'unknown',
  // 422 INCIDENT_IDEMPOTENCY_KEY_REUSED: la clave ya está usada en el backend
  // con otro payload o autor y no sirve más.
  keyReused: 'keyReused',
  // 400, 403 o 404: el backend los evalúa antes de mirar la clave (guard →
  // validación → visibilidad del paquete) y, con el mismo usuario y payload,
  // dan lo mismo cada vez: un envío dudoso idéntico anterior tampoco se creó.
  settled: 'settled',
  // Cualquier otro (429 del throttler, 408, 401 sin veredicto de sesión…): se
  // rechazó esta request y no dice nada de un envío anterior.
  transient: 'transient',
});

function classifySubmitFailure(error) {
  const status = apiErrorStatus(error);
  if (status === null || status >= 500) return SUBMIT_FAILURE.unknown;
  if (apiErrorCode(error) === INCIDENT_IDEMPOTENCY_KEY_REUSED) return SUBMIT_FAILURE.keyReused;
  if (status === 400 || status === 403 || status === 404) return SUBMIT_FAILURE.settled;
  return SUBMIT_FAILURE.transient;
}

/**
 * Formulario de alta de la mesa de incidencias (variant="desk" de IncidentForm).
 * Ver IncidentForm para el contrato de props.
 */
export default function DeskIncidentForm({
  packageId,
  followingNumber,
  origin,
  excludeTypes = [],
  onCreated,
  onUncertainOutcome,
  onCancel,
  hidden = false,
}) {
  const { t } = useTranslation();
  const fieldId = useId();
  const { keyFor, reset: resetIdempotencyKey } = useIdempotencyKey();
  const { recentTypes, recordRecentType } = useRecentIncidentTypes();

  const [incidentType, setIncidentType] = useState('');
  const [severity, setSeverity] = useState('');
  const [severityOverridden, setSeverityOverridden] = useState(false);
  const [responsible, setResponsible] = useState('');
  const [description, setDescription] = useState('');
  // Los errores de validación aparecen al primer intento de envío y desde ahí
  // se actualizan en vivo.
  const [validationVisible, setValidationVisible] = useState(false);
  // { error, uncertain, keyed, fingerprint } del último envío fallido. `keyed`:
  // si llevaba clave de idempotencia (sin crypto.randomUUID no la lleva).
  const [submitFailure, setSubmitFailure] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Corta el doble envío antes de que React re-renderice el botón.
  const inFlightRef = useRef(false);

  const titleId = `${fieldId}-title`;
  const typeInputId = `${fieldId}-type`;
  const typeErrorId = `${fieldId}-type-error`;
  const severityId = `${fieldId}-severity`;
  const severityHintId = `${fieldId}-severity-hint`;
  const responsibleId = `${fieldId}-responsible`;
  const descriptionId = `${fieldId}-description`;
  const descriptionErrorId = `${fieldId}-description-error`;

  const typeMissing = !incidentType || !selectableIncidentTypes(excludeTypes).includes(incidentType);
  const descriptionMissing = !description.trim();
  const typeError = validationVisible && typeMissing ? t('Elige el tipo de incidencia.') : null;
  const descriptionError = validationVisible && descriptionMissing ? t('Escribe una descripción.') : null;

  const fingerprint = buildFingerprint({
    packageId,
    type: incidentType,
    description,
    severity,
    responsible,
    origin,
  });
  // Hay un envío dudoso con este mismo payload, todavía sin aclarar.
  const hasPendingUncertainty =
    submitFailure?.uncertain === true && submitFailure.fingerprint === fingerprint;
  // "Reintentar" solo si reintentar no puede duplicar: mismo payload (si cambió,
  // es otro intento y lleva otra clave) y el envío dudoso llevaba clave.
  const canRetrySameAttempt = hasPendingUncertainty && submitFailure.keyed;

  const handleTypeChange = (nextType) => {
    setIncidentType(nextType);
    // Igual que el formulario clásico: al cambiar el tipo vuelve la criticidad
    // automática, aunque se hubiera cambiado a mano.
    setSeverity(getAutoSeverity(nextType));
    setSeverityOverridden(false);
  };

  const resetForm = () => {
    setIncidentType('');
    setSeverity('');
    setSeverityOverridden(false);
    setResponsible('');
    setDescription('');
    setValidationVisible(false);
    setSubmitFailure(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (inFlightRef.current) return;

    if (typeMissing || descriptionMissing) {
      setValidationVisible(true);
      document.getElementById(typeMissing ? typeInputId : descriptionId)?.focus();
      return;
    }

    // Se lee antes de limpiar el fallo: decide si la duda sigue en pie.
    const retryingUncertainAttempt = hasPendingUncertainty;

    inFlightRef.current = true;
    setSubmitting(true);
    setSubmitFailure(null);

    const idempotencyKey = keyFor(fingerprint);
    let response;
    try {
      response = await createPackageIncident(
        {
          packageId,
          severity: severity || undefined,
          responsible: responsible || undefined,
          type: incidentType,
          description: description.trim(),
          ...(origin ? { origin } : {}),
          ...(idempotencyKey ? { idempotencyKey } : {}),
        },
        // silent: el error se muestra en línea; sin esto saldría además el toast
        // del cliente HTTP.
        { silent: true, timeoutMs: INCIDENT_CREATE_TIMEOUT_MS },
      );
    } catch (error) {
      // createPackageIncident no lanza: esto es un fallo inesperado del cliente.
      // No se sabe si la request salió, así que se trata como resultado dudoso.
      console.error('Error submitting incident:', error);
      response = { success: false, data: null, error: null };
    }

    inFlightRef.current = false;
    setSubmitting(false);

    if (!response.success) {
      const failure = classifySubmitFailure(response.error);
      // La clave se descarta solo cuando ya no sirve. Tras cualquier otro
      // rechazo no quedó guardada y reusarla es inocuo; descartarla, en cambio,
      // haría que un reintento después de un envío dudoso que sí se creó
      // duplique la incidencia.
      if (failure === SUBMIT_FAILURE.keyReused) resetIdempotencyKey();
      const uncertain =
        failure === SUBMIT_FAILURE.unknown ||
        (retryingUncertainAttempt && failure === SUBMIT_FAILURE.transient);
      setSubmitFailure({ error: response.error, uncertain, keyed: Boolean(idempotencyKey), fingerprint });
      // Al final y sin try, como onCreated: el estado del formulario ya quedó.
      if (uncertain) onUncertainOutcome?.({ packageId });
      return;
    }

    resetIdempotencyKey();
    recordRecentType(incidentType);
    showToast(
      followingNumber
        ? t('Incidencia creada en el paquete #{{seguimiento}}', { seguimiento: followingNumber })
        : t('Incidencia creada correctamente'),
      true,
    );
    resetForm();
    // Al final y sin try: si el callback del contenedor falla, la incidencia
    // YA existe y no hay que mostrar un error que invite a crearla de nuevo.
    onCreated?.(response.data);
  };

  if (hidden) {
    return null;
  }

  const requiredDepartments = incidentType ? getRequiredDepartments(incidentType) : [];
  const showAutoSeverityHint = Boolean(incidentType) && !severityOverridden;

  let failureText = null;
  if (submitFailure?.uncertain) {
    // Mientras la duda siga en pie se habla de ella, no del último rechazo (un
    // 429 al reintentar no dice si el primer envío se creó).
    failureText = canRetrySameAttempt
      ? t("No pudimos confirmar si se creó. Revisa 'Incidencias de este pedido' o reintenta: no se duplicará.")
      : // Cambió el payload después del envío dudoso, o no llevaba clave: el
        // anterior pudo haberse creado y crear de nuevo podría duplicarla.
        t('No pudimos confirmar la incidencia. Revisa las incidencias de este pedido antes de volver a crearla.');
  } else if (submitFailure) {
    failureText =
      incidentsApiErrorText(submitFailure.error, t, { source: INCIDENTS_ERROR_SOURCE.create }) ??
      t('No se pudo crear la incidencia. Tus datos siguen en el formulario: inténtalo de nuevo.');
  }

  let originText = null;
  if (origin) {
    originText = origin.ticketNumber
      ? t('Origen: esta conversación · Ticket #{{ticket}}', { ticket: origin.ticketNumber })
      : t('Origen: esta conversación');
  }

  return (
    <form
      noValidate
      onSubmit={handleSubmit}
      aria-labelledby={titleId}
      aria-busy={submitting || undefined}
      className="flex flex-col"
    >
      <div className="flex flex-col gap-4 p-4">
        <h3 id={titleId} className="text-base font-semibold text-fg">
          {followingNumber
            ? t('Nueva incidencia · Pedido #{{seguimiento}}', { seguimiento: followingNumber })
            : t('Nueva incidencia')}
        </h3>

        <fieldset disabled={submitting} className="m-0 flex min-w-0 flex-col gap-4 border-0 p-0">
          <IncidentTypePicker
            value={incidentType}
            onChange={handleTypeChange}
            excludeTypes={excludeTypes}
            recentTypes={recentTypes}
            inputId={typeInputId}
            error={typeError}
            errorId={typeErrorId}
            disabled={submitting}
          />

          {requiredDepartments.length > 0 && (
            <div className="flex items-center gap-1.5 text-sm text-fg">
              <p>
                {t('Deben resolverla: {{areas}}', {
                  areas: requiredDepartments.map((dept) => t(DEPARTMENT_LABELS[dept] || dept)).join(' · '),
                })}
              </p>
              <PanelHint
                srLabel={t('Ayuda sobre las áreas')}
                label={t('Se asignan según el tipo; la incidencia se cierra cuando todas estas áreas marcan su parte como resuelta.')}
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <label htmlFor={severityId} className={FIELD_LABEL}>
                {t('Criticidad')}
              </label>
              <PanelHint
                srLabel={t('Ayuda sobre la criticidad')}
                label={t('Se completa sola según el tipo. Puedes cambiarla si el caso lo amerita.')}
              />
            </div>
            <Select
              id={severityId}
              value={severity}
              onChange={(event) => {
                setSeverity(event.target.value);
                setSeverityOverridden(true);
              }}
              placeholder={t('Selecciona una criticidad')}
              options={SEVERITY_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))}
              aria-describedby={showAutoSeverityHint ? severityHintId : undefined}
              className={cn('h-11 md:h-10', MOBILE_FIELD_TEXT)}
            />
            {showAutoSeverityHint && (
              <span id={severityHintId} className="text-xs text-fg-muted">
                {t('Asignada según el tipo')}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <label htmlFor={responsibleId} className={FIELD_LABEL}>
                {t('Responsable (opcional)')}
              </label>
              <PanelHint
                srLabel={t('Ayuda sobre el responsable')}
                label={t('No se puede cambiar después de crear la incidencia.')}
              />
            </div>
            <Select
              id={responsibleId}
              value={responsible}
              onChange={(event) => setResponsible(event.target.value)}
              placeholder={t('Sin responsable específico')}
              options={RESPONSIBLE_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))}
              className={cn('h-11 md:h-10', MOBILE_FIELD_TEXT)}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor={descriptionId} className={FIELD_LABEL}>
              {t('Descripción')}{' '}
              <span aria-hidden="true" className="text-status-error">
                *
              </span>
            </label>
            <Textarea
              id={descriptionId}
              rows={4}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t('Cuenta qué pasó, con los datos que te dio el contacto.')}
              aria-required="true"
              aria-invalid={descriptionError ? true : undefined}
              aria-describedby={descriptionError ? descriptionErrorId : undefined}
              invalid={Boolean(descriptionError)}
              className={MOBILE_FIELD_TEXT}
            />
            {descriptionError && (
              <p id={descriptionErrorId} role="alert" className="text-xs text-status-error">
                {descriptionError}
              </p>
            )}
          </div>

          {originText && (
            <div className="flex items-center gap-1.5 text-xs text-fg-muted">
              <span>{originText}</span>
              <PanelHint
                srLabel={t('Ayuda sobre el origen')}
                label={t('La incidencia queda vinculada a esta conversación y se deja una nota interna en el chat. El contacto no la ve.')}
              />
            </div>
          )}
        </fieldset>

        {failureText && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-status-error/40 bg-status-error-soft p-3 text-sm text-fg"
          >
            <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-status-error" />
            <p className="min-w-0">{failureText}</p>
          </div>
        )}
      </div>

      {/* Fuera del fieldset: el botón de envío no se deshabilita mientras
          envía (aria-disabled), así el foco no se pierde y "Reintentar" queda
          a mano en el mismo lugar. */}
      <div
        className={cn(
          'sticky bottom-0 z-[1] flex items-center gap-2 border-t border-border bg-bg px-4 py-3',
          onCancel ? 'justify-between' : 'justify-end',
        )}
      >
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={submitting} className="h-11 md:h-10">
            {t('Cancelar')}
          </Button>
        )}
        <Button
          type="submit"
          aria-disabled={submitting || undefined}
          className="h-11 bg-brand-700 hover:bg-brand-700 hover:opacity-90 active:bg-brand-700 aria-disabled:cursor-wait aria-disabled:opacity-60 dark:bg-brand-900 md:h-10"
        >
          {submitting && (
            <span
              aria-hidden="true"
              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none"
            />
          )}
          {submitting ? t('Creando…') : canRetrySameAttempt ? t('Reintentar') : t('Crear incidencia')}
        </Button>
      </div>
    </form>
  );
}
